import React, { useEffect } from 'react';
import { PageLayout } from '@src/common/PageLayout';
import { useBreadcrumbs } from '@src/state/breadcrumbs';
import { event } from '@uhg-abyss/web/tools/event';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Label } from '@uhg-abyss/web/ui/Label';
import { Card } from '@uhg-abyss/web/ui/Card';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Link } from '@uhg-abyss/web/ui/Link';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { MediaQuery } from '@uhg-abyss/web/ui/MediaQuery';
import { usePayer } from '@src/state/payer';
import { useDashboard } from '@src/state/dashboard';
import { PageBodyIntro } from '@workshop/common/PageBodyIntro';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';

const StyledLargeIconBrand = styled(IconBrand, {
  marginRight: '10px',
});

const StyledFlexLabel = styled(Flex, {
  marginBottom: '12px',
});

const StyledChatWithUsFlexLabel = styled(Flex, {
  marginTop: '30px',
  marginBottom: '12px',
});

const StyledColumn = styled(Flex, {
  marginBottom: '36px',
});
const StyledULOne = styled('ul', {
  listStyle: 'disc',
  marginLeft: '20px !important',
});
const StyledULTwo = styled('ul', {
  listStyle: 'disc',
  alignItems: 'left',
});
const StyledDivListContainer = styled('div', {
  padding: '10px',
});
const StyledDivChat = styled('div', {
  width: '43%',
  marginLeft: '20px',
  padding: '8px',
  fontWeight: 400,
  '@media (min-width: 1183px': {
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  '@media (max-width: 983px)': {
    width: '100%',
  },
});
const StyledLabelSubheading = styled(Label, {
  color: '$primary1!important',
  marginBottom: '15px',
  size: '$lg!important',
});
const StyledIconMaterialPhone = styled(IconMaterial, {
  marginRight: '10px',
  marginBottom: '-9px',
});
const StyledCard = styled(Card, {
  '&.abyss-card-root': {
    boxShadow: 'none',
    borderColor: '$gray5',
  },
});
const StyledCardSection = styled(Card.Section, {
  fontWeight: 400,
});
const StyledPageLayout = styled(PageLayout, {
  paddingBottom: '1px',
});
const StyledImg = styled('img', {
  minHeight: '100%',
  objectFit: 'cover',
});

export const ContactUsContent = () => {
  const { payer } = usePayer.state();
  const { setBreadcrumbs, breadcrumbs } = useBreadcrumbs();
  const { providerPreferenceComplete } = useDashboard.state();
  const { getAssetByKey } = usePPDLagoon();
  const chatTallAsset = getAssetByKey('CONTACT_US_CHAT_TALL_IMAGE');
  const chatWideAsset = getAssetByKey('CONTACT_US_CHAT_WIDE_IMAGE');

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);

    event.setData({ pageName: 'contact-us' });
    setBreadcrumbs({
      id: 'contact-us',
      title: 'Contact Us',
      href: '/contact-us',
    });
  }, []);

  useEffect(() => {
    if (payer.payerId && providerPreferenceComplete) {
      event('CONTACT_US_PAGE_LOAD');
    }
  }, [payer.payerId, providerPreferenceComplete]);

  return (
    <React.Fragment>
      <PageBodyIntro title='Contact us' breadcrumbs={breadcrumbs} />
      <StyledPageLayout>
        <StyledColumn direction='column'>
          <StyledChatWithUsFlexLabel alignItems='center'>
            <StyledLargeIconBrand size='38px' icon='chat' />
            <Label color='$primary1' size='$lg'>
              Chat with us
            </Label>
          </StyledChatWithUsFlexLabel>
          <StyledCard>
            <Card.Section
              css={{
                'abyss-card-section': {
                  padding: '0px',
                },
              }}
            >
              <Flex
                justify='space-between'
                alignItems='center'
                alignContent='center'
              >
                <StyledDivChat>
                  Skip the phone and start a chat with one of our provider
                  advocates. They can provide instant support on the following
                  topics:
                  <StyledDivListContainer>
                    <Grid>
                      <Grid.Col span='50%'>
                        <StyledULOne>
                          <li>
                            Joining the network <br />
                            (credentialing and contracting)
                          </li>
                          <li>Eligibility and benefits</li>
                        </StyledULOne>
                      </Grid.Col>
                      <Grid.Col span='50%'>
                        <StyledULTwo>
                          <li>Prior authorization</li>
                          <li>Claims</li>
                          <li>Portal technical support</li>
                        </StyledULTwo>
                      </Grid.Col>
                    </Grid>
                  </StyledDivListContainer>
                  Connect with us 24/7. To get started, select the chat button
                  at the bottom-right corner of the page.
                </StyledDivChat>
                <MediaQuery largerThan={984} smallerThan={1236}>
                  {chatTallAsset && (
                    <StyledImg src={chatTallAsset} alt='chatTall' />
                  )}
                </MediaQuery>
                <MediaQuery largerThan={1236}>
                  {chatWideAsset && <img src={chatWideAsset} alt='chat' />}
                </MediaQuery>
              </Flex>
            </Card.Section>
          </StyledCard>
        </StyledColumn>
        <StyledColumn direction='column'>
          <StyledFlexLabel alignItems='center'>
            <StyledLargeIconBrand size='38px' icon='phone' />
            <Label color='$primary1' size='$lg'>
              Call us
            </Label>
          </StyledFlexLabel>
          <StyledCard>
            <StyledCardSection>
              <StyledLabelSubheading size='$lg'>
                Accessibility Information
              </StyledLabelSubheading>
              We're committed to ensuring that our experiences are accessible to
              individuals with disabilities. If you need assistance using our
              website, mobile applications or a PDF on the website, we're here
              to help. <br />
              <br />
              <StyledIconMaterialPhone icon='phone' />
              Please call{' '}
              <Link fontWeight='$bold' href='tel:+8443867491'>
                844-386-7491, TTY 711
              </Link>
            </StyledCardSection>
          </StyledCard>
        </StyledColumn>
      </StyledPageLayout>
    </React.Fragment>
  );
};
